/**
 * IntradayChart.jsx
 *
 * Canvas-based 1-min intraday chart with:
 *  - Price line (blue) + BB bands (red upper, green lower, blue mid, light-blue fill)
 *  - Volume bars (green/red) below price
 *  - VWAP trend (smoothed, yellow/gold) as intraday trend indicator
 *  - RSI(14) purple + RSI signal 9-SMA yellow in a sub-panel
 *  - Mousewheel zoom on time axis, click+drag pan
 *  - Shows previous session when market is closed
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useIntradayChart } from '../../hooks/useIntradayChart';

// ── Color constants (matching CSS vars resolved to actual hex) ─────────────────
const C = {
  bg:         '#080d14',
  bgElev:     '#0f141c',
  border:     '#1e2633',
  borderStr:  '#2a3547',
  text:       '#e6edf3',
  muted:      '#8b949e',
  faint:      '#3d444d',
  up:         '#2ea043',
  upSoft:     'rgba(46,160,67,0.12)',
  down:       '#f85149',
  downSoft:   'rgba(248,81,73,0.12)',
  warn:       '#d29922',
  accent:     '#58a6ff',
  accentSoft: 'rgba(88,166,255,0.10)',
  // Chart-specific
  bbFill:     'rgba(88,166,255,0.08)',
  bbUp:       '#f85149',   // red upper band
  bbLo:       '#2ea043',   // green lower band
  bbMid:      '#58a6ff',   // blue mid
  price:      '#1f77ff',   // bold blue price line
  vwap:       '#d4a017',   // gold VWAP trend
  rsiLine:    '#9b59b6',   // purple RSI
  rsiSig:     '#f0c040',   // yellow RSI signal
  rsiZoneFill:'rgba(155,89,182,0.10)',
};

const PRICE_RATIO  = 0.62;  // price panel takes 62% of height
const VOL_RATIO    = 0.10;  // volume strip 10%
const RSI_RATIO    = 0.28;  // RSI panel 28%
const PRICE_PAD    = { top: 18, bottom: 4 };
const RSI_PAD      = { top: 10, bottom: 18 };
const Y_AXIS_W     = 56;
const X_AXIS_H     = 22;
const MIN_BARS_VIS = 20;
const MAX_BARS_VIS = 390;

function lerp(a, b, t) { return a + (b - a) * t; }

function niceStep(range, maxTicks) {
  const rough = range / maxTicks;
  const mag   = Math.pow(10, Math.floor(Math.log10(rough)));
  const steps = [1, 2, 2.5, 5, 10];
  for (const s of steps) if (s * mag >= rough) return s * mag;
  return steps[steps.length - 1] * mag;
}

export default function IntradayChart({ symbol, quote }) {
  const canvasRef    = useRef(null);
  const stateRef     = useRef({ offsetBars: 0, visibleBars: 120, dragging: false, dragStartX: 0, dragStartOff: 0 });
  const rafRef       = useRef(null);

  const [showBB,    setShowBB]    = useState(true);
  const [showSMA20, setShowSMA20] = useState(true);
  const [showVWAP,  setShowVWAP]  = useState(true);

  const { bars, sessionLabel, error, loading } = useIntradayChart(symbol, true);

  // ── Draw ──────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bars?.length) return;
    const dpr = window.devicePixelRatio || 1;
    const W   = canvas.clientWidth;
    const H   = canvas.clientHeight;
    if (W === 0 || H === 0) return;

    if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
      canvas.width  = W * dpr;
      canvas.height = H * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const st          = stateRef.current;
    const n           = bars.length;
    const visB        = Math.min(Math.max(Math.round(st.visibleBars), MIN_BARS_VIS), Math.min(MAX_BARS_VIS, n));
    const maxOff      = Math.max(0, n - visB);
    const off         = Math.min(Math.max(Math.round(st.offsetBars), 0), maxOff);
    st.offsetBars     = off;
    st.visibleBars    = visB;

    const slice       = bars.slice(off, off + visB);
    const chartW      = W - Y_AXIS_W;
    const priceH      = Math.floor(H * PRICE_RATIO) - X_AXIS_H;
    const volH        = Math.floor(H * VOL_RATIO);
    const rsiH        = H - priceH - volH - X_AXIS_H;
    const priceTop    = 0;
    const volTop      = priceH;
    const rsiTop      = priceH + volH;
    const barW        = chartW / visB;

    // ── Background ──
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, W, H);

    // ── Section dividers ──
    ctx.strokeStyle = C.border;
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, volTop);   ctx.lineTo(W, volTop);
    ctx.moveTo(0, rsiTop);   ctx.lineTo(W, rsiTop);
    ctx.moveTo(chartW, 0);   ctx.lineTo(chartW, H);
    ctx.stroke();

    // ─── Price panel ───────────────────────────────────────────────────────
    const closes  = slice.map(b => b.c).filter(Boolean);
    const bbUps   = slice.map(b => b.bbUp).filter(Boolean);
    const bbLos   = slice.map(b => b.bbLo).filter(Boolean);
    const vwaps   = slice.map(b => b.vwapTrend).filter(Boolean);
    const allPx   = [...closes, ...bbUps, ...bbLos, ...vwaps];
    if (!allPx.length) return;

    const rawMin  = Math.min(...allPx);
    const rawMax  = Math.max(...allPx);
    const pad     = (rawMax - rawMin) * 0.08 || 0.5;
    const pMin    = rawMin - pad;
    const pMax    = rawMax + pad;

    const py = v => priceTop + PRICE_PAD.top + (1 - (v - pMin) / (pMax - pMin)) * (priceH - PRICE_PAD.top - PRICE_PAD.bottom);
    const px = i => i * barW + barW / 2;

    // Grid lines on price panel
    const priceRange = pMax - pMin;
    const priceStep  = niceStep(priceRange, 6);
    ctx.strokeStyle  = C.border;
    ctx.lineWidth    = 0.5;
    ctx.fillStyle    = C.muted;
    ctx.font         = `9px monospace`;
    ctx.textAlign    = 'right';
    let gridP        = Math.ceil(pMin / priceStep) * priceStep;
    while (gridP <= pMax) {
      const y = py(gridP);
      if (y >= priceTop && y <= priceTop + priceH) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
        ctx.fillText(gridP.toFixed(2), W - 2, y + 3);
      }
      gridP += priceStep;
    }

    // Clip to price panel
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, priceTop, chartW, priceH);
    ctx.clip();

    // BB fill
    const firstBBIdx = slice.findIndex(b => b.bbUp != null && b.bbLo != null);
    if (showBB && firstBBIdx >= 0) {
      ctx.beginPath();
      ctx.moveTo(px(firstBBIdx), py(slice[firstBBIdx].bbUp));
      for (let i = firstBBIdx + 1; i < slice.length; i++) {
        if (slice[i].bbUp != null) ctx.lineTo(px(i), py(slice[i].bbUp));
      }
      for (let i = slice.length - 1; i >= firstBBIdx; i--) {
        if (slice[i].bbLo != null) ctx.lineTo(px(i), py(slice[i].bbLo));
      }
      ctx.closePath();
      ctx.fillStyle = C.bbFill;
      ctx.fill();
    }

    // Helper: draw a line series
    const drawLine = (key, color, width, dash = []) => {
      ctx.strokeStyle = color;
      ctx.lineWidth   = width;
      ctx.setLineDash(dash);
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < slice.length; i++) {
        const v = slice[i][key];
        if (v == null) { started = false; continue; }
        if (!started) { ctx.moveTo(px(i), py(v)); started = true; }
        else ctx.lineTo(px(i), py(v));
      }
      ctx.stroke();
      ctx.setLineDash([]);
    };

    // BB upper (red dashed), lower (green dashed), mid (blue dashed)
    if (showBB) {
      drawLine('bbUp',  C.bbUp,  1,   [4, 3]);
      drawLine('bbLo',  C.bbLo,  1,   [4, 3]);
    }
    if (showBB && showSMA20) drawLine('bbMid', C.bbMid, 0.7, [3, 4]);

    // VWAP trend (gold, smooth, slightly thicker)
    if (showVWAP) drawLine('vwapTrend', C.vwap, 1.5, []);

    // Price line (bold blue)
    drawLine('c', C.price, 2, []);

    ctx.restore();

    // Current price dashed reference line
    if (quote?.regularMarketPrice) {
      const cp = quote.regularMarketPrice;
      if (cp >= pMin && cp <= pMax) {
        const y = py(cp);
        ctx.strokeStyle = C.accent;
        ctx.lineWidth   = 0.8;
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle   = C.accent;
        ctx.fillRect(chartW, y - 8, Y_AXIS_W, 16);
        ctx.fillStyle   = C.bg;
        ctx.font        = 'bold 9px monospace';
        ctx.textAlign   = 'left';
        ctx.fillText(cp.toFixed(2), chartW + 3, y + 3);
      }
    }

    // ─── Volume panel ──────────────────────────────────────────────────────
    const vols    = slice.map(b => b.v || 0);
    const maxVol  = Math.max(...vols, 1);
    ctx.save();
    ctx.beginPath(); ctx.rect(0, volTop, chartW, volH); ctx.clip();

    for (let i = 0; i < slice.length; i++) {
      const b    = slice[i];
      const h    = (b.v / maxVol) * (volH - 2);
      const isUp = b.c >= b.o;
      ctx.fillStyle = isUp ? 'rgba(46,160,67,0.55)' : 'rgba(248,81,73,0.55)';
      const bx = i * barW;
      const bw = Math.max(barW - 1, 1);
      ctx.fillRect(bx, volTop + volH - h, bw, h);
    }
    ctx.restore();

    // Volume axis label
    ctx.fillStyle = C.faint;
    ctx.font      = '8px monospace';
    ctx.textAlign = 'right';
    const maxVolFmt = maxVol >= 1e6 ? (maxVol/1e6).toFixed(1)+'M' : maxVol >= 1e3 ? (maxVol/1e3).toFixed(0)+'K' : String(maxVol);
    ctx.fillText(maxVolFmt, W - 2, volTop + 10);

    // ─── RSI panel ─────────────────────────────────────────────────────────
    const rsiMin = 0, rsiMax = 100;
    const ry     = v => rsiTop + RSI_PAD.top + (1 - (v - rsiMin) / (rsiMax - rsiMin)) * (rsiH - RSI_PAD.top - RSI_PAD.bottom);

    // Overbought/oversold zones
    ctx.fillStyle = 'rgba(248,81,73,0.06)';
    ctx.fillRect(0, ry(100), chartW, ry(70) - ry(100));
    ctx.fillStyle = 'rgba(46,160,67,0.06)';
    ctx.fillRect(0, ry(30), chartW, ry(0) - ry(30));
    // Purple background tint overall
    ctx.fillStyle = C.rsiZoneFill;
    ctx.fillRect(0, rsiTop, chartW, rsiH);

    // RSI grid
    ctx.strokeStyle = C.border;
    ctx.lineWidth   = 0.5;
    ctx.fillStyle   = C.muted;
    ctx.font        = '9px monospace';
    ctx.textAlign   = 'right';
    for (const level of [30, 50, 70]) {
      const y = ry(level);
      ctx.setLineDash(level === 50 ? [2, 4] : [3, 3]);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(chartW, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText(String(level), W - 2, y + 3);
    }

    // RSI line (purple) + signal (yellow)
    ctx.save();
    ctx.beginPath(); ctx.rect(0, rsiTop, chartW, rsiH); ctx.clip();

    const drawRsiLine = (key, color, width) => {
      ctx.strokeStyle = color;
      ctx.lineWidth   = width;
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < slice.length; i++) {
        const v = slice[i][key];
        if (v == null) { started = false; continue; }
        const y = ry(v);
        if (!started) { ctx.moveTo(px(i), y); started = true; }
        else ctx.lineTo(px(i), y);
      }
      ctx.stroke();
    };

    // Fill under RSI line (soft purple)
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < slice.length; i++) {
      const v = slice[i].rsi;
      if (v == null) continue;
      if (!started) { ctx.moveTo(px(i), ry(v)); started = true; }
      else ctx.lineTo(px(i), ry(v));
    }
    // close down to bottom
    ctx.lineTo(px(slice.length - 1), ry(0) + 2);
    ctx.lineTo(px(firstBBIdx >= 0 ? slice.findIndex(b => b.rsi != null) : 0), ry(0) + 2);
    ctx.closePath();
    ctx.fillStyle = 'rgba(155,89,182,0.12)';
    ctx.fill();

    drawRsiLine('rsiSig', C.rsiSig, 1.2);
    drawRsiLine('rsi',    C.rsiLine, 1.5);

    ctx.restore();

    // ─── X-axis (time labels) ──────────────────────────────────────────────
    const xAxisY  = priceH;
    ctx.fillStyle = C.bgElev;
    ctx.fillRect(0, xAxisY, chartW, X_AXIS_H);
    ctx.fillStyle = C.muted;
    ctx.font      = '9px monospace';
    ctx.textAlign = 'center';

    const labelEvery = Math.max(1, Math.floor(visB / 8));
    for (let i = 0; i < slice.length; i++) {
      if (i % labelEvery !== 0) continue;
      const t = slice[i].time;
      if (!t) continue;
      ctx.fillText(t, px(i), xAxisY + 14);
    }

    // ─── Y-axis background ─────────────────────────────────────────────────
    ctx.fillStyle = C.bgElev;
    ctx.fillRect(chartW, 0, Y_AXIS_W, H);

    // BB/price labels on right axis
    const labelCandidates = [
      { key: 'bbUp',  color: C.bbUp,  label: b => b.bbUp?.toFixed(2) },
      { key: 'bbLo',  color: C.bbLo,  label: b => b.bbLo?.toFixed(2) },
      { key: 'c',     color: C.price, label: b => b.c?.toFixed(2), bold: true },
      { key: 'vwapTrend', color: C.vwap, label: b => b.vwapTrend?.toFixed(2) },
    ];
    const last = slice[slice.length - 1];
    if (last) {
      for (const { key, color, label, bold } of labelCandidates) {
        const v = last[key];
        if (v == null || v < pMin || v > pMax) continue;
        const y = py(v);
        ctx.fillStyle = color;
        ctx.fillRect(chartW, y - 8, Y_AXIS_W, 16);
        ctx.fillStyle = C.bg;
        ctx.font      = bold ? 'bold 9px monospace' : '9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(label(last), chartW + 3, y + 3);
      }
    }

    // ─── RSI right-axis value labels (drawn last so they sit on top of bg) ──
    if (last) {
      if (last.rsi != null) {
        const ry_rsi = ry(Math.max(0, Math.min(100, last.rsi)));
        ctx.fillStyle = C.rsiLine;
        ctx.fillRect(chartW, ry_rsi - 8, Y_AXIS_W, 16);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(last.rsi.toFixed(1), chartW + 3, ry_rsi + 3);
      }
      if (last.rsiSig != null) {
        const ry_sig = ry(Math.max(0, Math.min(100, last.rsiSig)));
        const rsi_y  = last.rsi != null ? ry(Math.max(0, Math.min(100, last.rsi))) : null;
        const nudge  = rsi_y != null && Math.abs(ry_sig - rsi_y) < 10 ? 12 : 0;
        ctx.fillStyle = C.rsiSig;
        ctx.fillRect(chartW, ry_sig - 8 + nudge, Y_AXIS_W, 16);
        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(last.rsiSig.toFixed(1), chartW + 3, ry_sig + 3 + nudge);
      }
    }
  }, [bars, quote, showBB, showSMA20, showVWAP]);

  // ── Resize observer ───────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  // ── Redraw when data changes ───────────────────────────────────────────────
  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [draw, bars]);

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const st  = stateRef.current;
    const n   = bars?.length || 0;
    if (!n) return;
    const factor = e.deltaY > 0 ? 1.12 : 0.89;
    const oldVis = st.visibleBars;
    const newVis = Math.min(Math.max(oldVis * factor, MIN_BARS_VIS), Math.min(MAX_BARS_VIS, n));

    // Zoom centered on mouse x position
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const chartW = rect.width - Y_AXIS_W;
    const mouseBarFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / chartW));
    const focusBar = st.offsetBars + mouseBarFrac * oldVis;
    st.visibleBars = newVis;
    st.offsetBars  = Math.max(0, focusBar - mouseBarFrac * newVis);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [bars, draw]);

  // ── Pan (drag) ────────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    const st = stateRef.current;
    st.dragging    = true;
    st.dragStartX  = e.clientX;
    st.dragStartOff= st.offsetBars;
  }, []);

  const onMouseMove = useCallback((e) => {
    const st = stateRef.current;
    if (!st.dragging || !bars?.length) return;
    const canvas = canvasRef.current;
    const chartW = canvas.clientWidth - Y_AXIS_W;
    const barsPerPx = st.visibleBars / chartW;
    const delta  = (e.clientX - st.dragStartX) * barsPerPx;
    st.offsetBars = Math.max(0, Math.min(bars.length - st.visibleBars, st.dragStartOff - delta));
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [bars, draw]);

  const onMouseUp = useCallback(() => {
    stateRef.current.dragging = false;
  }, []);

  // ── Wheel event (non-passive) ─────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // ── Status states ─────────────────────────────────────────────────────────
  if (!symbol) return <div className="empty">Select a symbol.</div>;
  if (loading)  return <div className="empty">Loading 1-min data…</div>;
  if (error)    return <div className="err-msg">⚠ {error}</div>;
  if (!bars?.length) return <div className="empty">No intraday data available.</div>;

  const last     = bars[bars.length - 1];
  const isLive   = sessionLabel === 'TODAY';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', userSelect: 'none' }}>

      {/* ── Header ── */}
      <div style={{
        padding: '5px 10px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap',
        fontSize: 10, fontFamily: 'monospace',
      }}>
        <span style={{ color: 'var(--muted)', letterSpacing: '.06em' }}>
          1-MIN · {bars.length} bars
          {isLive
            ? <span style={{ color: 'var(--up)', marginLeft: 6 }}>● LIVE</span>
            : <span style={{ color: 'var(--warn)', marginLeft: 6 }}>◎ {sessionLabel}</span>
          }
        </span>

        {/* Indicator toggles */}
        {[
          [showBB,    setShowBB,    'BB(20,1σ)', C.bbUp],
          [showSMA20, setShowSMA20, 'SMA20',     C.bbMid],
          [showVWAP,  setShowVWAP,  'VWAP',      C.vwap],
        ].map(([on, setter, label, color]) => (
          <button key={label} onClick={() => setter(v => !v)} style={{
            padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
            fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
            letterSpacing: '.04em', border: `1px solid ${on ? color : 'var(--border-strong)'}`,
            background: on ? color + '22' : 'transparent',
            color: on ? color : 'var(--faint)',
            transition: 'all .15s',
          }}>{label}</button>
        ))}

        {/* Live values */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {last?.vwapTrend != null && (
            <span><span style={{ color: 'var(--faint)' }}>VWAP </span>
              <span style={{ color: C.vwap, fontWeight: 700 }}>{last.vwapTrend.toFixed(2)}</span>
            </span>
          )}
          {last?.rsi != null && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: 'var(--faint)' }}>RSI</span>
              <span style={{ color: C.rsiLine, fontWeight: 700 }}>{last.rsi.toFixed(1)}</span>
              {last?.rsiSig != null && (
                <span style={{ color: C.rsiSig, fontWeight: 700, fontSize: 9 }}>/ {last.rsiSig.toFixed(1)}</span>
              )}
              {last.rsi >= 70 && <span style={{ color: C.down, fontSize: 9 }}>OBOUGHT</span>}
              {last.rsi <= 30 && <span style={{ color: C.up,   fontSize: 9 }}>OSOLD</span>}
            </span>
          )}
        </div>

        <span style={{ color: 'var(--faint)', fontSize: 9 }}>scroll=zoom · drag=pan</span>
      </div>

      {/* ── Canvas ── */}
      <canvas
        ref={canvasRef}
        style={{ flex: 1, minHeight: 0, width: '100%', display: 'block', cursor: stateRef.current.dragging ? 'grabbing' : 'crosshair' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      />
    </div>
  );
}
